// DTOs для REST API между apps/bot и apps/webapp.
// Держим в sync с packages/db/schema.ts.

export type Role = 'admin' | 'consultant' | 'intern' | 'banned' | 'guest' | 'paused';

// ── Тесты: единый источник правды для проходного балла (шкала 0..10) ──
// Импортируется и ботом (apps/bot/src/http/quiz.ts), и webapp (LessonPage/QuizPage),
// чтобы проходной балл в логике и в UI-тексте не расходились.
export const QUIZ_PASS_SCORE = 7;
export const EXAM_PASS_SCORE = 7;
/** Проходной балл в процентах для UI-текстов (7 → 70). */
export const passScorePct = (score: number): number => Math.round(score * 10);

export interface MeDTO {
  id: string; // bigint как string
  tgId: string;
  tgUsername: string | null;
  firstName: string | null;
  fullName: string | null;
  role: Role;
  onboardingDone: boolean;
  curriculumStartedAt: string | null;
  lastSeenAt: string | null;
  /** Авто-приостановлен за неактивность — фронт показывает экран «доступ приостановлен». */
  suspended: boolean;
}

export interface CurriculumDTO {
  id: string;
  name: string;
  version: number;
  lessons: LessonBriefDTO[];
}

export interface LessonBriefDTO {
  id: string;
  dayNumber: number;
  title: string;
  emoji: string | null;
  description: string | null;
  estimatedMin: number | null;
  status: 'locked' | 'available' | 'in_progress' | 'completed' | 'failed';
  testScore: number | null;
}

export interface LessonFullDTO extends LessonBriefDTO {
  articles: ArticleDTO[];
  quizQuestionsCount: number;
}

export interface ArticleDTO {
  id: string;
  lessonId: string | null;
  position: number;
  title: string;
  bodyMd: string;
  summaryMd: string | null;
  mediaUrls: MediaUrl[];
  tags: string[];
  isStandalone: boolean;
  isBookmarked?: boolean;
}

export interface MediaUrl {
  type: 'image' | 'video' | 'pdf' | 'video_s3';
  url?: string;
  bucket?: string;
  key?: string;
  title?: string;
  duration?: number;
}

export interface SearchResultDTO {
  articles: ArticleDTO[];
  query: string;
  tookMs: number;
}

export interface QuizQuestionDTO {
  id: string;
  lessonId: string;
  position: number;
  questionMd: string;
  /** Multiple-choice варианты (4 шт) или null для free-form */
  options: string[] | null;
  isFinalExam: boolean;
}

export interface QuizAttemptDTO {
  id: string;
  lessonId: string;
  attemptNo: number;
  isFinalExam: boolean;
  startedAt: string;
  completedAt: string | null;
  scoreAvg: number | null;
  passed: boolean | null;
  answers: QuizAnswerDTO[];
}

export interface QuizAnswerDTO {
  questionId: string;
  userAnswer: string;
  aiScore: number | null;
  aiFeedback: string | null;
}

// ── Quiz/Exam: response DTOs (НЕ путать с QuizAnswerDTO выше — то persisted-форма
// ответа внутри QuizAttemptDTO.answers; ниже — формы HTTP-ответов эндпоинтов) ──

/**
 * Ответ POST /api/training/attempts/:id/answer — оценка одного вопроса.
 * - score: 0..10
 * - correct: для multiple-choice (selectedOption === correctOption); undefined для free-form
 * - correctOption: 0-based индекс правильного варианта (только MC) — чтобы UI подсветил
 *   верный ответ; undefined для free-form или если у вопроса нет вариантов.
 */
export interface QuizAnswerResultDTO {
  questionId: string;
  score: number;
  feedback: string;
  correct?: boolean;
  correctOption?: number;
}

/**
 * Ответ POST /api/training/attempts/:id/finish — итог попытки.
 * - nextLessonId: id следующего урока, разблокированного при passed; null если не сдал
 *   или это последний урок / финальный экзамен.
 * - attemptNo: номер этой попытки (1-based).
 * - prevBestScore: лучший scoreAvg по предыдущим завершённым попыткам этого урока (0..10);
 *   null если это первая завершённая попытка.
 */
export interface QuizFinishDTO {
  ok: true;
  scoreAvg: number;
  passed: boolean;
  nextLessonId: string | null;
  attemptNo: number;
  prevBestScore: number | null;
}

/**
 * Ответ GET /api/training/lessons/:id/exam-summary — данные для pre-exam sheet.
 * - attemptsUsed: сколько завершённых попыток экзамена уже сделано.
 * - totalQuestions: размер набора вопросов экзамена (обычно 15).
 * - passPct: проходной балл в процентах (см. passScorePct(EXAM_PASS_SCORE) → 70).
 * - prevBestScore: лучший scoreAvg по предыдущим попыткам (0..10) или null.
 */
export interface ExamSummaryDTO {
  attemptsUsed: number;
  totalQuestions: number;
  passPct: number;
  prevBestScore: number | null;
}

/**
 * Элемент GET /api/training/recent-views — недавно просмотренные статьи.
 * - lessonDay: dayNumber урока, к которому привязана статья; null для standalone-статей.
 */
export interface RecentViewDTO {
  articleId: string;
  title: string;
  lessonDay: number | null;
}

// AI Chat
export interface AiThreadDTO {
  id: string;
  mode: 'chat' | 'test' | 'exam' | 'practice' | 'lesson_qa';
  title: string | null;
  createdAt: string;
  messagesCount: number;
}

export interface AiMessageDTO {
  id: string;
  role: 'system' | 'user' | 'assistant';
  content: string;
  createdAt: string;
}

// Admin
export interface AdminUserListDTO {
  users: AdminUserBriefDTO[];
  total: number;
}

export interface AdminUserBriefDTO {
  id: string;
  tgUsername: string | null;
  firstName: string | null;
  role: Role;
  lastSeenAt: string | null;
  curriculumProgress: {
    day: number;
    total: number;
    percent: number;
  } | null;
}

export interface AdminUserDetailDTO extends AdminUserBriefDTO {
  tgId: string;
  createdAt: string;
  invitedBy: string | null;
  stats: {
    quizzesPassed: number;
    quizzesTotal: number;
    quizAvgScore: number | null;
    searchQueries7d: number;
    bookmarks: number;
    activeMinutes: number;
  };
  recentActions: UserActionDTO[];
}

export interface UserActionDTO {
  id: string;
  action: string;
  payload: unknown;
  createdAt: string;
}

export interface FalBalanceDTO {
  balanceUsd: number;
  asOf: string;
  spent: {
    today: number;
    week: number;
    month: number;
  };
  threshold: number;
  thresholdBreached: boolean;
}

export interface ForwardingSettingsDTO {
  enabled: boolean;
  groupId: number;
  modules: Array<'content_studio' | 'photo_studio' | 'ai_chat' | 'training'>;
  exclusions: string[];
}

// API envelope
export interface ApiError {
  error: string;
  code?: string;
  details?: unknown;
}

export interface ApiResponse<T> {
  ok: true;
  data: T;
}

export type ApiResult<T> = ApiResponse<T> | { ok: false; error: ApiError };

// ── График смен (табель выхода) ──
export type ShiftSlot = 'A' | 'B';

/**
 * Базовый распорядок выхода (структура 2-недельного цикла).
 * Индексы 0..6 = Пн..Вс первой недели, 7..13 = второй недели.
 * A/B — это «первый»/«второй» консультант; кто именно A и B, задаёт админ.
 * Текущая ротация магазина: A = «Давид нов» (3+4 дня), B = «Дьякон» (4+3 дня).
 */
export const SHIFT_ROTATION_PRESET: ShiftSlot[] = [
  'B', 'B', 'A', 'A', 'B', 'A', 'B', // нед.1: Пн Вт Ср Чт Пт Сб Вс
  'B', 'A', 'A', 'B', 'B', 'A', 'A', // нед.2: Пн Вт Ср Чт Пт Сб Вс
];

export interface ShiftRotation {
  /** ISO 'YYYY-MM-DD', по конвенции — понедельник. null = распорядок не настроен. */
  anchorDate: string | null;
  /** userId (bigint как string). null = не выбран. */
  consultantA: string | null;
  consultantB: string | null;
  /** Длина = 14; каждый слот = 'A' | 'B'. */
  pattern: ShiftSlot[];
}

export interface ShiftDayDTO {
  date: string; // 'YYYY-MM-DD'
  userId: string | null;
  userName: string | null;
  isOverride: boolean;
}

export interface ShiftConsultantDTO {
  id: string;
  name: string;
}

// ── Контроль сроков товара ──
export type ExpiryStageKey = 'expired' | 'd3' | 'd14' | 'd31' | 'd38' | 'd45' | 'd60' | 'watch';

export interface ExpiryItemDTO {
  category: string;
  name: string;
  expiryDate: string;   // 'YYYY-MM-DD'
  daysLeft: number;
  stage: ExpiryStageKey;
  stageLabel: string;
  discountPct: number;
  actions: string[];
}

export interface ExpiryListDTO {
  items: ExpiryItemDTO[];
  skipped: number;      // строк отброшено при парсинге
  fetchedAt: string;    // ISO timestamp
  error?: string;       // если источник недоступен
}
